// ─────────────────────────────────────────────────────────────────────────────
// DomainDashboard — Stage 3 generic per-domain dashboard (B3/B4).
//
// ONE domain-agnostic component for the user domains (PERSONAL/TRADING/BUSINESS
// and the non-reportable RESEARCH/FRONTIER). Replaces the "COMING SOON"
// placeholder bodies. CODE keeps CodePage, SECURITY keeps SecurityPage (the B5
// reference dashboards) — they never route here.
//
// GROWTH NO LONGER ROUTES HERE (v4 contract §3.2, Sherman-signed). It declares a
// nine-root vocabulary and has its own built surface, so the backend rejects
// GET /api/dashboard/domain/GROWTH with a 400 — this component would have
// rendered that 400 as "not_available". The route decides (DomainDashboardScreen);
// no domain is named in here, which is why the fix is a routing change and not a
// guard clause. A domain that declares roots in future goes the same way.
//
// Domain = sum of its agents: fetches GET /api/dashboard/domain/<DOMAIN> and
// renders the real Stage-1 activity tier (ALWAYS — even when Stage-2 transcript
// content is still empty, per Ghost amendment 1) + a per-agent grid of recent
// session content (paragraph + cite-able key-claims). Honest empty-state per
// agent until the Stage-2 transcript write lands at the integration checkpoint.
//
// READ-ONLY. No writes, no synthesis (synthesis = Stage 8), no persistence
// (Stage 5), no self-read loop (Stage 4). Palette: REXY_CONSOLE only.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../../lib/rexyConsole';

const MONO = REXY_FONT;

// Tier → accent. not_observable/dormant recede; live tiers brighten with load.
const TIER_COLOR: Record<string, string> = {
  high: REXY_CONSOLE.gold,
  med: REXY_CONSOLE.primary,
  low: REXY_CONSOLE.mutedText,
  dormant: REXY_CONSOLE.dimText,
  not_observable: REXY_CONSOLE.dimText,
};

interface ClaimView { claim_id: string; claim: string; locator: string; }
interface SessionView { session_id: string; ts: string | number | null; paragraph: string | null; claims: ClaimView[]; }
interface AgentView { agent: string; session_count: number; claim_count: number; last_active_ts: string | number | null; recent_sessions: SessionView[]; }
interface DomainView {
  domain: string;
  reportable: boolean;
  tier: string;
  rate_per_hr: number | null;
  content_available: boolean;
  agents: AgentView[];
  aggregate: { agent_count: number; session_count: number; claim_count: number; last_active_ts: string | number | null };
  content_note: string | null;
}

function TierChip({ tier, rate }: { tier: string; rate: number | null }) {
  const color = TIER_COLOR[tier] || REXY_CONSOLE.dimText;
  return (
    <span style={{ ...styles.tierChip, color, borderColor: color }}>
      {tier}
      {typeof rate === 'number' ? ` · ${rate.toFixed(2)}/hr` : ''}
    </span>
  );
}

function AgentCard({ a }: { a: AgentView }) {
  const hasContent = a.recent_sessions.length > 0;
  return (
    <div style={styles.agentCard}>
      <div style={styles.agentHeader}>
        <span style={styles.agentName}>{a.agent}</span>
        <span style={styles.agentMeta}>
          {a.session_count} session{a.session_count === 1 ? '' : 's'} · {a.claim_count} claim{a.claim_count === 1 ? '' : 's'}
        </span>
      </div>
      {!hasContent ? (
        <div style={styles.agentEmpty}>no session transcripts yet</div>
      ) : (
        <div style={styles.sessionList}>
          {a.recent_sessions.map((s, i) => (
            <div key={s.session_id || `s${i}`} style={styles.session}>
              {s.paragraph && <div style={styles.paragraph}>{s.paragraph}</div>}
              {s.claims.length > 0 && (
                <div style={styles.claimList}>
                  {s.claims.map((c, j) => (
                    <div key={c.claim_id || `c${j}`} style={styles.claim} title={c.locator}>
                      • {c.claim}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DomainDashboard({ domain, authToken }: { domain: string; authToken?: string | null }) {
  const [view, setView] = useState<DomainView | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const token = authToken || (window as any).__rexyToken;
    setView(null);
    setErr(null);
    fetch(`/api/dashboard/domain/${encodeURIComponent(domain)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.json())
      .then((j) => (j && j.success ? setView(j.domain_view) : setErr((j && j.error) || 'unavailable')))
      .catch((e) => setErr(String(e)));
  }, [domain, authToken]);

  return (
    <div style={styles.root}>
      <div style={styles.topRow}>
        <span style={styles.label}>{domain} DASHBOARD</span>
        {view && <TierChip tier={view.tier} rate={view.rate_per_hr} />}
        {view && !view.reportable && <span style={styles.nonReport}>tier/status only — non-reportable</span>}
      </div>

      {err ? (
        <div style={styles.degraded}>not_available — {err}</div>
      ) : !view ? (
        <div style={styles.degraded}>loading…</div>
      ) : (
        <>
          <div style={styles.aggregate}>
            {view.aggregate.agent_count} agents · {view.aggregate.session_count} sessions · {view.aggregate.claim_count} claims
          </div>
          {view.content_note && <div style={styles.note}>{view.content_note}</div>}
          {view.agents.length === 0 ? (
            <div style={styles.degraded}>no agents registered for this domain</div>
          ) : (
            <div style={styles.grid}>
              {view.agents.map((a) => (
                <AgentCard key={a.agent} a={a} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root: { flex: 1, display: 'flex', flexDirection: 'column', fontFamily: MONO, gap: 12, minHeight: 0, overflow: 'auto' },
  topRow: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  label: { fontFamily: MONO, fontSize: 13, letterSpacing: 2, color: REXY_CONSOLE.primary },
  tierChip: {
    fontFamily: MONO, fontSize: 10, letterSpacing: 1, padding: '2px 8px',
    border: '1px solid', borderRadius: 4, textTransform: 'uppercase',
  },
  nonReport: { fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: REXY_CONSOLE.dimText },
  aggregate: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.infText },
  note: { fontFamily: MONO, fontSize: 9, letterSpacing: 0.5, color: REXY_CONSOLE.dimText },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 },
  agentCard: {
    border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    background: REXY_CONSOLE.cardBg,
    borderRadius: 6,
    padding: 12,
    // Dimensional, never flat (CLAUDE.md UI §3) — same idiom as DashboardColumns.
    boxShadow: '0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(0,191,255,0.06)',
  },
  agentHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 8 },
  agentName: { fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: REXY_CONSOLE.primary },
  agentMeta: { fontFamily: MONO, fontSize: 8, color: REXY_CONSOLE.dimText, whiteSpace: 'nowrap' },
  agentEmpty: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.dimText, padding: '4px 0' },
  sessionList: { display: 'flex', flexDirection: 'column', gap: 8 },
  session: { borderTop: `1px solid ${REXY_CONSOLE.rowDivider}`, paddingTop: 6 },
  paragraph: { fontFamily: MONO, fontSize: 10, lineHeight: 1.45, color: REXY_CONSOLE.infText, marginBottom: 4 },
  claimList: { display: 'flex', flexDirection: 'column', gap: 2 },
  claim: { fontFamily: MONO, fontSize: 9, lineHeight: 1.4, color: REXY_CONSOLE.mutedText },
  degraded: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.dimText, padding: '6px 0' },
};
